// Composer.tsx — interactive "Ask a trainer..." composer for the Petcademy
// Messages thread. Phase 2 of the read-only thread: lets a pet parent type a
// question, attach photos & videos (file picker, drag-and-drop, or paste),
// preview them, and send. Token-driven so it travels into the same partner
// embeds as the thread. Ported from the Claude Design prototype (Composer.jsx),
// with two real-world additions over the prototype: a hard 10-item attachment
// cap (ticket AC9) and carrying the underlying File object so the host can
// upload the bytes (the prototype only kept local object URLs).
import React, {
  useRef, useState, useEffect,
} from 'react';
import Icon from './Icon';

// A single attachment held in the composer tray before send. `file` is the raw
// File (uploaded by the host); `url` is a local object URL used only for preview.
export interface ComposerAttachment {
  id: string;
  kind: 'image' | 'video';
  file: File;
  url: string;
  name: string;
  status: 'processing' | 'ready';
  poster?: string;
  duration?: string;
}

// What `onSend` receives: trimmed text + the ready attachments (raw files +
// locally-derived metadata). The host uploads each file and persists the message.
export interface ComposerSendPayload {
  text: string;
  media: ComposerAttachment[];
}

export interface ComposerApi {
  addFiles: (files: FileList | File[]) => void;
}

interface ComposerProps {
  // Returns a promise; the composer clears only when it resolves, so a failed
  // send keeps the user's draft + attachments intact (QA 10.2).
  onSend: (payload: ComposerSendPayload) => Promise<void>;
  onReady?: (api: ComposerApi) => void;
  placeholder?: string;
  // Host-driven send error, surfaced beneath the composer (announced to AT).
  sendError?: string | null;
  // Optional analytics hooks (embed). Absent in the Hub.
  onStart?: () => void; // once per mount, on the textarea's first focus or change
  onAttachmentAdded?: (kind: 'image' | 'video') => void; // once per file actually attached
}

const PC_MAX_CHARS = 3000;
const PC_MAX_MEDIA = 10;
const PC_MAX_FILE_BYTES = 50 * 1024 * 1024; // Zendesk attachment limit (50 MB)

function formatDuration(seconds: number): string | null {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const total = Math.round(seconds);
  const mins = Math.floor(total / 60);
  return `${mins}:${String(total % 60).padStart(2, '0')}`;
}

// Grab a poster frame + duration from a video file (decoded locally, no upload).
function videoThumb(url: string): Promise<{ poster?: string; duration?: string }> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    let done = false;
    const finish = (d: { poster?: string; duration?: string }) => {
      if (done) return;
      done = true;
      resolve(d);
    };
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = url;
    const grab = () => {
      try {
        const w = video.videoWidth || 320;
        const h = video.videoHeight || 240;
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        const ctx = c.getContext('2d');
        if (ctx) ctx.drawImage(video, 0, 0, w, h);
        finish({ poster: c.toDataURL('image/jpeg', 0.82), duration: formatDuration(video.duration) || undefined });
      } catch (e) {
        finish({ duration: formatDuration(video.duration) || undefined });
      }
    };
    video.onloadeddata = () => {
      try {
        video.currentTime = Math.min(0.4, (video.duration || 1) / 3);
      } catch (e) {
        grab();
      }
    };
    video.onseeked = grab;
    video.onerror = () => finish({});
    setTimeout(() => finish({ duration: formatDuration(video.duration) || undefined }), 4000);
  });
}

const isMediaFile = (f: File) => !!f && (f.type.startsWith('image/') || f.type.startsWith('video/'));

/* ---- attachment thumbnail ---- */
function AttThumb({ att, onRemove }: { att: ComposerAttachment; onRemove: (id: string) => void }) {
  const video = att.kind === 'video';
  let preview;
  if (!video) {
    preview = <img src={att.url} alt={att.name || ''} />;
  } else if (att.poster) {
    preview = <img src={att.poster} alt="" />;
  } else {
    preview = <span className="pc-thumb__videobg" aria-hidden="true" />;
  }
  return (
    <div className={`pc-thumb pc-thumb--${att.kind}`}>
      {preview}
      {video && att.status === 'ready' && (
        <span className="pc-thumb__play" aria-hidden="true"><Icon name="play_arrow" size={16} /></span>
      )}
      {video && att.duration && att.status === 'ready' && (
        <span className="pc-thumb__dur">{att.duration}</span>
      )}
      {att.status === 'processing' && (
        <span className="pc-thumb__proc" aria-label="Processing"><span className="pc-spinner" /></span>
      )}
      <button
        type="button"
        className="pc-thumb__remove"
        aria-label={`Remove ${att.name || 'attachment'}`}
        onClick={() => onRemove(att.id)}
      >
        <Icon name="close" size={14} />
      </button>
    </div>
  );
}

/* ---- Composer ---- */
function Composer({
  onSend, onReady, placeholder = 'Write a message...', sendError = null, onStart, onAttachmentAdded,
}: ComposerProps) {
  const [text, setText] = useState('');
  const [atts, setAtts] = useState<ComposerAttachment[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [capHit, setCapHit] = useState(false);
  const [sizeHit, setSizeHit] = useState(false);
  const [busy, setBusy] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const attsRef = useRef(atts);
  attsRef.current = atts;
  // addFiles is captured once by onReady, so hooks are read through refs.
  const onAttachmentAddedRef = useRef(onAttachmentAdded);
  onAttachmentAddedRef.current = onAttachmentAdded;
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;
  const startedRef = useRef(false);
  const markStarted = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (onStartRef.current) onStartRef.current();
  };

  const addFiles = (fileList: FileList | File[]) => {
    const media = Array.from(fileList || []).filter(isMediaFile);
    // Reject anything over Zendesk's 50 MB limit before it can be attached/sent.
    const files = media.filter((f) => f.size <= PC_MAX_FILE_BYTES);
    setSizeHit(media.length > files.length);
    // Which files fit is decided here (not in the updater) so the analytics hook
    // never runs inside a state updater.
    const room0 = Math.max(0, PC_MAX_MEDIA - attsRef.current.length);
    if (onAttachmentAddedRef.current) {
      files.slice(0, room0).forEach((f) => {
        onAttachmentAddedRef.current?.(f.type.startsWith('video/') ? 'video' : 'image');
      });
    }
    setAtts((prev) => {
      const room = PC_MAX_MEDIA - prev.length;
      if (files.length > room) setCapHit(true);
      const accepted = files.slice(0, Math.max(0, room));
      const next = accepted.map((file) => {
        const id = `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        const url = URL.createObjectURL(file);
        const kind: 'image' | 'video' = file.type.startsWith('video/') ? 'video' : 'image';
        if (kind === 'video') {
          videoThumb(url).then((info) => setAtts((a) => a.map(
            (x) => (x.id === id ? { ...x, ...info, status: 'ready' } : x),
          )));
        }
        return {
          id, kind, file, url, name: file.name, status: kind === 'video' ? 'processing' : 'ready',
        } as ComposerAttachment;
      });
      return [...prev, ...next];
    });
  };

  // Expose addFiles upward so the thread can route drag-and-drop here.
  useEffect(() => {
    if (onReady) onReady({ addFiles });
    return () => {
      // revoke any unsent object URLs on unmount
      attsRef.current.forEach((a) => { try { URL.revokeObjectURL(a.url); } catch (e) { /* noop */ } });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once the user drops back below the cap, allow the notice to re-fire later.
  useEffect(() => { if (atts.length < PC_MAX_MEDIA) setCapHit(false); }, [atts.length]);

  const removeAtt = (id: string) => {
    // Removing an attachment is a corrective action — clear the transient
    // size/over-cap notices so a stale error doesn't linger (QA: error should
    // disappear once the offending item is removed).
    setSizeHit(false);
    setCapHit(false);
    setAtts((a) => {
      const hit = a.find((x) => x.id === id);
      if (hit) { try { URL.revokeObjectURL(hit.url); } catch (e) { /* noop */ } }
      return a.filter((x) => x.id !== id);
    });
  };

  const clearAll = () => {
    attsRef.current.forEach((a) => { try { URL.revokeObjectURL(a.url); } catch (e) { /* noop */ } });
    setAtts([]);
    setCapHit(false);
    setSizeHit(false);
  };

  // "2 photos · 1 video" style summary for the tray header (omits empty kinds).
  const photoCount = atts.filter((a) => a.kind === 'image').length;
  const videoCount = atts.filter((a) => a.kind === 'video').length;
  const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
  const traySummary = [
    photoCount ? plural(photoCount, 'photo') : '',
    videoCount ? plural(videoCount, 'video') : '',
  ].filter(Boolean).join(' · ');

  const processing = atts.some((a) => a.status === 'processing');
  const ready = atts.filter((a) => a.status === 'ready');
  const len = text.length;
  const atLimit = len >= PC_MAX_CHARS;
  const nearLimit = len > PC_MAX_CHARS - 200;
  const canSend = (text.trim().length > 0 || ready.length > 0) && !processing && !busy;
  const atMediaCap = atts.length >= PC_MAX_MEDIA;

  const grow = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  };

  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    try {
      await onSend({ text: text.trim(), media: ready });
      // Success — clear. The sent message renders from the host (server URLs),
      // so the local preview URLs are safe to revoke.
      ready.forEach((a) => { try { URL.revokeObjectURL(a.url); } catch (e) { /* noop */ } });
      setText('');
      setAtts((a) => a.filter((x) => x.status === 'processing'));
      setTruncated(false);
      setCapHit(false);
      setSizeHit(false);
      if (taRef.current) { taRef.current.style.height = 'auto'; taRef.current.focus(); }
    } catch (e) {
      // Keep the draft + attachments; the host surfaces the error via sendError.
    } finally {
      setBusy(false);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    markStarted();
    if (truncated) setTruncated(false);
    let v = e.target.value;
    if (v.length > PC_MAX_CHARS) v = v.slice(0, PC_MAX_CHARS);
    setText(v);
    grow(e.target);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const cd = e.clipboardData;
    if (!cd) return;
    // media files take priority — route them to the attachment tray
    const files: File[] = [];
    Array.from(cd.items).forEach((it) => {
      if (it.kind === 'file') { const f = it.getAsFile(); if (f) files.push(f); }
    });
    if (files.length) { e.preventDefault(); addFiles(files); return; }
    // text paste — clip to the limit if it would overflow
    const pasted = cd.getData('text');
    if (!pasted) return;
    const el = taRef.current;
    const start = el ? el.selectionStart : text.length;
    const end = el ? el.selectionEnd : text.length;
    const merged = text.slice(0, start) + pasted + text.slice(end);
    if (merged.length > PC_MAX_CHARS) {
      e.preventDefault();
      const clipped = merged.slice(0, PC_MAX_CHARS);
      const caret = Math.min(start + pasted.length, PC_MAX_CHARS);
      setText(clipped);
      setTruncated(true);
      requestAnimationFrame(() => {
        if (taRef.current) {
          grow(taRef.current);
          try { taRef.current.setSelectionRange(caret, caret); } catch (e2) { /* noop */ }
          taRef.current.focus();
        }
      });
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const fieldError = atLimit || truncated;

  const renderMetaError = () => {
    if (truncated) {
      return (
        <span className="pc-composer__error" role="alert">
          <Icon name="error" size={15} />
          Pasted text was shortened to fit the 3,000 character limit.
        </span>
      );
    }
    if (atLimit) {
      return (
        <span className="pc-composer__error" role="alert">
          <Icon name="error" size={15} />
          You’ve reached the 3,000 character limit.
        </span>
      );
    }
    return <span />;
  };

  return (
    <div className="pc-composer pc-composer--live">
      {atts.length > 0 && (
        <div className="pc-trayblock">
          <div className="pc-tray__head">
            <div className="pc-tray__meta">
              <span className="pc-tray__summary">{traySummary}</span>
              <span className="pc-tray__count">
                {atts.length}
                /
                {PC_MAX_MEDIA}
              </span>
            </div>
            <button type="button" className="pc-tray__clear" onClick={clearAll}>Clear all</button>
          </div>
          <div className="pc-tray" aria-label="Attachments">
            {atts.map((a) => <AttThumb key={a.id} att={a} onRemove={removeAtt} />)}
          </div>
        </div>
      )}

      <div className="pc-composer__row">
        <button
          type="button"
          className="pc-iconbtn pc-iconbtn--attach"
          aria-label="Attach photos or videos"
          disabled={atMediaCap}
          onClick={() => fileRef.current && fileRef.current.click()}
        >
          <Icon name="attach_file" size={24} />
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*"
          multiple
          hidden
          onChange={(e) => { addFiles(e.target.files || []); e.target.value = ''; }}
        />

        <div className={`pc-composer__field pc-composer__field--live${fieldError ? ' pc-composer__field--error' : ''}`}>
          <textarea
            ref={taRef}
            className="pc-composer__input"
            rows={1}
            maxLength={PC_MAX_CHARS}
            placeholder={placeholder}
            value={text}
            onChange={onChange}
            onFocus={markStarted}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
          />
        </div>

        <button
          type="button"
          className={`pc-iconbtn pc-iconbtn--send${canSend ? ' is-active' : ''}`}
          aria-label="Send message"
          disabled={!canSend}
          onClick={send}
        >
          {busy ? <span className="pc-spinner" /> : <Icon name="send" size={22} />}
        </button>
      </div>

      {(nearLimit || truncated) && (
        <div className={`pc-composer__meta${fieldError ? ' pc-composer__meta--error' : ''}`}>
          {renderMetaError()}
          <span className="pc-composer__count">
            {len.toLocaleString()}
            {' / '}
            {PC_MAX_CHARS.toLocaleString()}
          </span>
        </div>
      )}

      {sizeHit && (
        <div className="pc-composer__hint pc-composer__hint--error" role="alert">
          <Icon name="error" size={15} />
          Your file is too large. Max size: 50 MB.
        </div>
      )}

      {capHit && (
        <div className="pc-composer__hint pc-composer__hint--error" role="alert">
          <Icon name="error" size={15} />
          You can attach up to
          {' '}
          {PC_MAX_MEDIA}
          {' '}
          photos and videos.
        </div>
      )}

      {sendError && (
        <div className="pc-composer__hint pc-composer__hint--error" role="alert">
          <Icon name="error" size={15} />
          {sendError}
        </div>
      )}

      {processing && (
        <div className="pc-composer__hint">
          <span className="pc-spinner pc-spinner--dark" />
          Preparing video…
        </div>
      )}
    </div>
  );
}

export default Composer;
